import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { allChatTypeOptions, chatTypeOptions } from '../../helpers/MonitorTypeHelpers';
import CheckboxSelect, { CheckboxSelectOption } from '../checkbox-select/CheckboxSelect';
import DropdownItem from '../dropdown-item/DropdownItem';
import { useAccount } from '../../contexts/account/AccountContexts';
import { useEffectiveFeatures } from '../../contexts/effective-features/EffectiveFeaturesContext';
import { ChatType } from '../../types/monitor/ChatType';
import { PRICING } from '../../routes/path';

/**
 * Modele widoczne w filtrze. `available` to modele, które konto odpytuje, `all` dokłada zamknięte
 * (pokazywane z kłódką pod upsell). Wystawione osobno, bo rodzic bywa potrzebuje `available` do
 * własnych decyzji (np. „wszystkie zaznaczone" = brak parametru w URL-u).
 */
/**
 * `featuresOverride` podaje modele wprost — używa go okno generowania raportu, gdzie lista MUSI być
 * listą właściciela projektu. Kontekst `EffectiveFeaturesProvider` daje to samo, ale tylko dopóki
 * komponent renderuje się pod nim; jawny prop nie zależy od miejsca w drzewie.
 */
export const useModelSelectOptions = (featuresOverride?: string[]) => {
  const { accountResponse } = useAccount();
  // Modele właściciela, gdy superadmin ogląda projekt klienta; własne uprawnienia w pozostałych.
  const contextFeatures = useEffectiveFeatures();
  const effectiveFeatures = (featuresOverride?.length
    ? featuresOverride
    : contextFeatures) as typeof contextFeatures;

  const available = useMemo(
    () => (accountResponse ? chatTypeOptions(effectiveFeatures).filter((opt) => opt.type !== 'ALL') : []),
    [accountResponse, effectiveFeatures]
  );

  const all = useMemo(
    () =>
      accountResponse
        ? allChatTypeOptions(effectiveFeatures).filter((opt) => opt.type !== 'ALL' && !opt.separator)
        : [],
    [accountResponse, effectiveFeatures]
  );

  return { available, all, availableTypes: available.map((opt) => opt.type) };
};

interface ModelSelectProps {
  selected: ChatType[];
  /** Dostaje pełną nową listę; ostatniego modelu nie da się odznaczyć. */
  onChange: (types: ChatType[]) => void;
  /** Modele do wyboru, gdy nie mają pochodzić z konta zalogowanego — patrz `useModelSelectOptions`. */
  features?: string[];
}

/**
 * Dropdown wyboru modeli z filtrów panelu — mechanikę (panel, wiersz „wszystkie", checkboxy) daje
 * `CheckboxSelect`, a tutaj zostaje to, co modelowe: ikony, kłódki pod upsell i skrót „only".
 */
const ModelSelect: React.FC<ModelSelectProps> = ({ selected, onChange, features }) => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const { available, all, availableTypes } = useModelSelectOptions(features);

  const isAllSelected = selected.length >= availableTypes.length;
  const canManage = accountResponse?.role === 'OWNER' || accountResponse?.role === 'ADMIN';

  const triggerLabel = useMemo(() => {
    if (isAllSelected) return t('all');
    if (selected.length === 1) {
      return available.find((opt) => opt.type === selected[0])?.name || t('all');
    }
    return `${selected.length} ${t('modelsSelected')}`;
  }, [isAllSelected, selected, available, t]);

  const triggerIcon = useMemo(() => {
    if (isAllSelected || selected.length !== 1) return 'ico--models ico--globe';
    return available.find((opt) => opt.type === selected[0])?.icon || 'ico--models ico--globe';
  }, [isAllSelected, selected, available]);

  const options = useMemo<CheckboxSelectOption<ChatType>[]>(
    () =>
      all
        // Model spoza konta pokazujemy TYLKO gdy jest zamknięty (upsell); reszta nie ma po co wisieć.
        .filter((option) => availableTypes.includes(option.type) || option.locked)
        .map((option) => {
          const isChecked = selected.includes(option.type);
          const lockedTooltip = option.locked
            ? canManage
              ? 'upgradeToAccess'
              : 'upgradeToAccessContact'
            : undefined;

          return {
            value: option.type,
            label: option.name,
            disabled: !!option.locked,
            content: (
              <DropdownItem
                icon={option.icon}
                name={option.name}
                tooltipRef={option.type}
                tooltipText={lockedTooltip}
                tooltipLink={option.locked && canManage ? PRICING : undefined}
                isDisabled={!!option.locked}
                isNew={!option.locked && option.type === 'AI_MODE_BROWSER'}
              />
            ),
            action:
              !option.locked && !(isChecked && selected.length === 1) ? (
                // Skrót „tylko ten" — izoluje model bez ręcznego odznaczania reszty.
                <button
                  type="button"
                  className="checkbox-select__only"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([option.type]);
                  }}
                >
                  {t('modelSelectOnly')}
                </button>
              ) : undefined,
          };
        }),
    [all, availableTypes, selected, canManage, onChange, t]
  );

  if (!accountResponse) {
    return null;
  }

  return (
    <CheckboxSelect
      selected={selected}
      onChange={onChange}
      options={options}
      allLabel={t('all')}
      allContent={<DropdownItem icon="ico--models ico--globe" name={t('all')} />}
      triggerContent={<DropdownItem icon={triggerIcon} name={triggerLabel} />}
    />
  );
};

export default ModelSelect;
